import React, { useEffect, useState } from "react";
import {
    Plus, Pencil, Trash2, X, Star, ShoppingCart,
    Lightbulb, Volume2, Monitor, Settings2, Zap, Music,
    Headphones, Speaker, Radio, Cpu, Wifi, Camera, Film, Package, Tag, Mic2
} from "lucide-react";
import { api, BASE } from "../../services/api";
import ImageUpload from "../../components/ui/ImageUpload";

const ICON_MAP = {
    Lightbulb, Volume2, Monitor, Settings2, Zap, Music,
    Headphones, Speaker, Radio, Cpu, Wifi, Camera, Film, Package, Tag, Star, Mic2
};
const ICON_OPTIONS = Object.keys(ICON_MAP);
const COLOR_PRESETS = [
    "#00BFFF", "#a855f7", "#f59e0b", "#22c55e",
    "#ef4444", "#f97316", "#0ea5e9", "#ec4899",
    "#14b8a6", "#6366f1", "#84cc16", "#e879f9",
];

const EMPTY = { brand: "", model: "", category: "", type: "", qty: "", specs: [], image_url: "", badge: "", featured: false, rentable: false, rental_price_day: "", rental_qty: "", deposit: 0 };
const BADGE_PRESETS = ["Najlepší na SK", "Jediný na SK", "Novinka", "Top produkt", "Limitovaná edícia"];

function SpecsEditor({ specs, onChange }) {
    const add    = ()        => onChange([...specs, { k: "", v: "" }]);
    const remove = (i)       => onChange(specs.filter((_, idx) => idx !== i));
    const set    = (i, k, v) => onChange(specs.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Parametre</label>
                <button type="button" onClick={add} className="text-nova-cyan text-xs font-mono hover:underline">+ pridať</button>
            </div>
            <div className="space-y-1.5">
                {specs.map((s, i) => (
                    <div key={i} className="flex gap-2 items-center">
                        <input value={s.k} onChange={e => set(i, "k", e.target.value)} placeholder="Výkon"
                            className="w-1/3 h-8 px-2 rounded bg-nova-black border border-white/10 text-white text-xs focus:outline-none focus:border-nova-cyan/60" />
                        <input value={s.v} onChange={e => set(i, "v", e.target.value)} placeholder="500 W"
                            className="flex-1 h-8 px-2 rounded bg-nova-black border border-white/10 text-white text-xs focus:outline-none focus:border-nova-cyan/60" />
                        <button type="button" onClick={() => remove(i)} className="text-zinc-600 hover:text-red-400 transition-colors">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
                {specs.length === 0 && <p className="text-zinc-600 text-xs">Žiadne parametre.</p>}
            </div>
        </div>
    );
}

function Modal({ item, cats, onClose, onSave }) {
    const normalize = (src) => ({
        ...src,
        rental_price_day: src.rental_price_day != null && src.rental_price_day > 0 ? src.rental_price_day : "",
        rental_qty: src.rental_qty != null && src.rental_qty > 0 ? src.rental_qty : "",
    });
    const defaultCat = cats.length > 0 ? cats[0].slug : "";
    const [form, setForm] = useState(item ? normalize(item) : { ...EMPTY, category: defaultCat });
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState("");
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.brand || !form.model) { setError("Vyplňte značku a model."); return; }
        setSaving(true);
        const payload = {
            ...form,
            rental_price_day: form.rental_price_day === "" ? null : form.rental_price_day,
            rental_qty:       form.rental_qty       === "" ? null : form.rental_qty,
        };
        try { await onSave(payload); }
        catch (err) { setError(err.message); setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative bg-nova-surface border border-white/10 rounded-xl w-full max-w-lg p-6 z-10 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-heading font-bold text-white">{item ? "Upraviť zariadenie" : "Nové zariadenie"}</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={submit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Značka" value={form.brand} onChange={v => set("brand", v)} placeholder="Robe" />
                        <Field label="Model"  value={form.model} onChange={v => set("model", v)} placeholder="BMFL Blade" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Kategória</label>
                            <select value={form.category} onChange={e => set("category", e.target.value)}
                                className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60">
                                {cats.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                            </select>
                        </div>
                        <Field label={<>Množstvo <span className="text-zinc-700 normal-case font-sans font-normal">(nepovinné)</span></>} value={form.qty} onChange={v => set("qty", v)} placeholder="× 6" />
                    </div>
                    <Field label="Typ" value={form.type} onChange={v => set("type", v)} placeholder="Moving Head Beam/Wash" />
                    <ImageUpload value={form.image_url || ""} onChange={v => set("image_url", v)} />

                    {/* Badge */}
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Badge <span className="text-zinc-700 normal-case">(nepovinné)</span></label>
                        <input type="text" value={form.badge || ""} onChange={e => set("badge", e.target.value)}
                            placeholder="napr. Jediný na SK"
                            className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60 mb-2" />
                        <div className="flex flex-wrap gap-1.5">
                            {BADGE_PRESETS.map(p => (
                                <button key={p} type="button"
                                    onClick={() => set("badge", form.badge === p ? "" : p)}
                                    className={`px-2.5 py-1 rounded-full text-[10px] font-mono border transition-all ${
                                        form.badge === p
                                            ? "bg-nova-cyan/20 border-nova-cyan/40 text-nova-cyan"
                                            : "border-white/10 text-zinc-500 hover:border-white/25 hover:text-zinc-300"
                                    }`}>
                                    {p}
                                </button>
                            ))}
                            {form.badge && !BADGE_PRESETS.includes(form.badge) && (
                                <button type="button" onClick={() => set("badge", "")}
                                    className="px-2.5 py-1 rounded-full text-[10px] font-mono border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-all">
                                    Zmazať
                                </button>
                            )}
                        </div>
                    </div>

                    <SpecsEditor specs={form.specs} onChange={v => set("specs", v)} />

                    {/* ── Rental section ── */}
                    <div className="pt-3 border-t border-white/8">
                        <div className="flex items-center justify-between mb-3">
                            <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                                <ShoppingCart className="w-3 h-3 text-nova-cyan" /> Prenájom
                            </label>
                            <button type="button" onClick={() => set("rentable", !form.rentable)}
                                className={`relative w-10 h-5 rounded-full transition-colors ${form.rentable ? "bg-nova-cyan" : "bg-white/10"}`}>
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${form.rentable ? "left-5" : "left-0.5"}`} />
                            </button>
                        </div>
                        {form.rentable && (
                            <div className="space-y-2.5 p-3 rounded-xl bg-nova-black/40 border border-white/8">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Cena/deň (€)</label>
                                        <input type="number" min="0" step="1" placeholder="Cena na dopyt"
                                            value={form.rental_price_day ?? ""}
                                            onChange={e => set("rental_price_day", e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
                                            className="w-full h-8 px-2 rounded-lg bg-nova-black border border-white/10 text-white text-xs focus:outline-none focus:border-nova-cyan/60 placeholder:text-zinc-700" />
                                    </div>
                                    <div>
                                        <label className="block font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Počet ks na sklade</label>
                                        <input type="number" min="1" step="1" placeholder="Neobmedzene"
                                            value={form.rental_qty ?? ""}
                                            onChange={e => set("rental_qty", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                            className="w-full h-8 px-2 rounded-lg bg-nova-black border border-white/10 text-white text-xs focus:outline-none focus:border-nova-cyan/60 placeholder:text-zinc-700" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block font-mono text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Záloha (€)</label>
                                    <input type="number" min="0" step="1" value={form.deposit}
                                        onChange={e => set("deposit", parseFloat(e.target.value)||0)}
                                        className="w-full h-8 px-2 rounded-lg bg-nova-black border border-white/10 text-white text-xs focus:outline-none focus:border-nova-cyan/60" />
                                </div>
                                <p className="text-zinc-600 text-[10px]">Prázdna cena = zákazník dostane cenovú ponuku. Prázdny počet = neobmedzene kusov.</p>
                            </div>
                        )}
                    </div>

                    {error && <p className="text-red-400 text-xs font-mono">{error}</p>}
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors">Zrušiť</button>
                        <button type="submit" disabled={saving} className="flex-1 h-10 rounded-md bg-nova-cyan text-black font-semibold text-sm disabled:opacity-60">
                            {saving ? "Ukladám…" : "Uložiť"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder }) {
    return (
        <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</label>
            <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60" />
        </div>
    );
}

const CAT_EMPTY = { slug: "", label: "", color: "#00BFFF", icon_name: "Settings2", order: 99 };

function CatModal({ cat, onClose, onSave }) {
    const [form, setForm] = useState(cat || CAT_EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const isNew = !cat;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.slug || !form.label) { setError("Vyplňte slug a názov."); return; }
        if (!/^[a-z0-9_-]+$/.test(form.slug)) { setError("Slug môže obsahovať len malé písmená, čísla, - a _."); return; }
        setSaving(true);
        try { await onSave(form); }
        catch (err) { setError(err.message); setSaving(false); }
    };

    const PreviewIcon = ICON_MAP[form.icon_name] || Settings2;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative bg-nova-surface border border-white/10 rounded-xl w-full max-w-sm p-6 z-10">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-heading font-bold text-white">{isNew ? "Nová kategória" : "Upraviť kategóriu"}</h2>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={submit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Slug <span className="text-zinc-700 normal-case font-sans">(ID)</span></label>
                            <input type="text" value={form.slug} onChange={e => set("slug", e.target.value)}
                                disabled={!isNew}
                                placeholder="napr. osvetlenie"
                                className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60 disabled:opacity-50 disabled:cursor-not-allowed" />
                        </div>
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Poradie</label>
                            <input type="number" min="1" value={form.order} onChange={e => set("order", parseInt(e.target.value)||99)}
                                className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60" />
                        </div>
                    </div>
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Názov</label>
                        <input type="text" value={form.label} onChange={e => set("label", e.target.value)}
                            placeholder="napr. Osvetlenie"
                            className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60" />
                    </div>
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Ikona</label>
                        <select value={form.icon_name} onChange={e => set("icon_name", e.target.value)}
                            className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60">
                            {ICON_OPTIONS.map(name => <option key={name} value={name}>{name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Farba</label>
                        <div className="flex items-center gap-3">
                            <input type="color" value={form.color} onChange={e => set("color", e.target.value)}
                                className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10" />
                            <div className="flex flex-wrap gap-1.5">
                                {COLOR_PRESETS.map(c => (
                                    <button key={c} type="button" onClick={() => set("color", c)}
                                        className={`w-6 h-6 rounded-full border-2 transition-all ${form.color === c ? "border-white scale-110" : "border-transparent hover:border-white/50"}`}
                                        style={{ background: c }} />
                                ))}
                            </div>
                        </div>
                    </div>
                    {/* Preview */}
                    <div className="p-3 rounded-xl bg-nova-black/40 border border-white/8 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: `${form.color}20`, border: `1px solid ${form.color}40` }}>
                            <PreviewIcon className="w-4 h-4" style={{ color: form.color }} />
                        </div>
                        <div>
                            <div className="text-white text-sm font-medium">{form.label || "Názov kategórie"}</div>
                            <div className="font-mono text-[10px] text-zinc-500">{form.slug || "slug"}</div>
                        </div>
                    </div>

                    {error && <p className="text-red-400 text-xs font-mono">{error}</p>}
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors">Zrušiť</button>
                        <button type="submit" disabled={saving} className="flex-1 h-10 rounded-md bg-nova-cyan text-black font-semibold text-sm disabled:opacity-60">
                            {saving ? "Ukladám…" : "Uložiť"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CategoriesTab({ cats, onReload }) {
    const [modal, setModal]       = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [delError, setDelError] = useState("");

    const save = async (form) => {
        if (modal === "new") await api.createTechnikaCategory(form);
        else await api.updateTechnikaCategory(modal.slug, form);
        setModal(null);
        onReload();
    };

    const del = async (slug) => {
        setDelError("");
        try {
            await api.deleteTechnikaCategory(slug);
            setDeleting(null);
            onReload();
        } catch (err) {
            setDelError(err.message);
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <p className="text-zinc-500 text-sm">Kategórie zariadení zobrazované na stránkach Technika a Prenájom.</p>
                <button onClick={() => setModal("new")}
                    className="flex items-center gap-2 h-9 px-4 bg-nova-cyan text-black rounded-md text-sm font-semibold hover:saturate-125 transition-all">
                    <Plus className="w-4 h-4" /> Pridať
                </button>
            </div>

            <div className="border border-white/8 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-white/8 bg-nova-surface/30">
                            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Kategória</th>
                            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hidden sm:table-cell">Slug</th>
                            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hidden sm:table-cell">Poradie</th>
                            <th className="px-4 py-3 w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {cats.map((c, i) => {
                            const Icon = ICON_MAP[c.icon_name] || Settings2;
                            return (
                                <tr key={c.slug} className={`border-b border-white/5 ${i % 2 === 0 ? "" : "bg-white/2"} hover:bg-white/4 transition-colors`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                                style={{ background: `${c.color}20`, border: `1px solid ${c.color}40` }}>
                                                <Icon className="w-4 h-4" style={{ color: c.color }} />
                                            </div>
                                            <span className="font-medium text-white">{c.label}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                        <span className="font-mono text-[10px] uppercase tracking-wider text-nova-cyan border border-nova-cyan/20 px-2 py-0.5 rounded-sm">{c.slug}</span>
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell font-mono text-xs text-zinc-500">{c.order}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2 justify-end">
                                            <button onClick={() => setModal(c)} className="p-1.5 text-zinc-500 hover:text-white transition-colors">
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => { setDeleting(c.slug); setDelError(""); }} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {cats.length === 0 && (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-600 text-sm">Žiadne kategórie.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {modal && <CatModal cat={modal === "new" ? null : modal} onClose={() => setModal(null)} onSave={save} />}

            {deleting && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setDeleting(null)} />
                    <div className="relative bg-nova-surface border border-white/10 rounded-xl p-6 max-w-sm w-full z-10">
                        <h2 className="font-heading font-bold text-white mb-2">Odstrániť kategóriu?</h2>
                        <p className="text-zinc-400 text-sm mb-3">Kategóriu nie je možné zmazať, ak obsahuje zariadenia.</p>
                        {delError && <p className="text-red-400 text-xs font-mono mb-3">{delError}</p>}
                        <div className="flex gap-3">
                            <button onClick={() => setDeleting(null)} className="flex-1 h-10 rounded-md border border-white/15 text-zinc-300 text-sm hover:bg-white/5">Zrušiť</button>
                            <button onClick={() => del(deleting)} className="flex-1 h-10 rounded-md bg-red-500 text-white font-semibold text-sm hover:bg-red-600">Odstrániť</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminTechnika() {
    const [items, setItems]         = useState([]);
    const [cats, setCats]           = useState([]);
    const [loading, setLoading]     = useState(true);
    const [modal, setModal]         = useState(null);
    const [deleting, setDeleting]   = useState(null);
    const [filterCat, setFilterCat] = useState("all");
    const [activeTab, setActiveTab] = useState("items");

    const loadItems = () => api.getTechnika().then(setItems).finally(() => setLoading(false));
    const loadCats  = () => api.getTechnikaCategories().then(setCats).catch(() => {});

    useEffect(() => { loadItems(); loadCats(); }, []);

    const save = async (form) => {
        if (modal === "new") await api.createEquipment(form);
        else await api.updateEquipment(modal.id, form);
        setModal(null);
        loadItems();
    };

    const del = async (id) => {
        await api.deleteEquipment(id);
        setDeleting(null);
        loadItems();
    };

    const filtered = filterCat === "all" ? items : items.filter(i => i.category === filterCat);

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-1">/ Technika</div>
                    <h1 className="font-heading text-2xl font-black tracking-tight">Správa techniky</h1>
                </div>
                {activeTab === "items" && (
                    <button onClick={() => setModal("new")}
                        className="flex items-center gap-2 h-9 px-4 bg-nova-cyan text-black rounded-md text-sm font-semibold hover:saturate-125 transition-all">
                        <Plus className="w-4 h-4" /> Pridať
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 border-b border-white/8">
                {[["items", "Zariadenia"], ["categories", "Kategórie"]].map(([id, label]) => (
                    <button key={id} onClick={() => setActiveTab(id)}
                        className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
                            activeTab === id
                                ? "text-white border-nova-cyan"
                                : "text-zinc-500 border-transparent hover:text-zinc-300"
                        }`}>
                        {label}
                    </button>
                ))}
            </div>

            {activeTab === "categories" ? (
                <CategoriesTab cats={cats} onReload={loadCats} />
            ) : (
                <>
                    {/* Category filter */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {[{ slug: "all", label: "Všetko" }, ...cats].map(c => (
                            <button key={c.slug} onClick={() => setFilterCat(c.slug)}
                                className={`px-3 py-1.5 rounded-sm font-mono text-xs uppercase tracking-wide border transition-all ${
                                    filterCat === c.slug
                                        ? "border-nova-cyan/60 text-nova-cyan bg-nova-cyan/5"
                                        : "border-white/8 text-zinc-500 hover:border-white/20"
                                }`}>
                                {c.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="text-zinc-500 text-sm font-mono">Načítanie…</div>
                    ) : (
                        <div className="border border-white/8 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/8 bg-nova-surface/30">
                                        <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Zariadenie</th>
                                        <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hidden sm:table-cell">Kategória</th>
                                        <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hidden md:table-cell">Množstvo</th>
                                        <th className="px-4 py-3 w-20"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((item, i) => (
                                        <tr key={item.id} className={`border-b border-white/5 ${i % 2 === 0 ? "" : "bg-white/2"} hover:bg-white/4 transition-colors`}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    {item.image_url ? (
                                                        <img
                                                            src={item.image_url.startsWith("http") ? item.image_url : `${BASE}${item.image_url}`}
                                                            alt={item.brand}
                                                            className="w-10 h-10 rounded-md object-cover border border-white/10 shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-md bg-white/4 border border-white/8 shrink-0" />
                                                    )}
                                                    <div>
                                                        <div className="font-medium text-white">{item.brand}</div>
                                                        <div className="text-zinc-500 text-xs">{item.model}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 hidden sm:table-cell">
                                                <span className="font-mono text-[10px] uppercase tracking-wider text-nova-cyan border border-nova-cyan/20 px-2 py-0.5 rounded-sm">{item.category}</span>
                                            </td>
                                            <td className="px-4 py-3 hidden md:table-cell font-mono text-xs text-zinc-400">{item.qty}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2 justify-end">
                                                    <button onClick={() => setModal(item)} className="p-1.5 text-zinc-500 hover:text-white transition-colors">
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button onClick={() => setDeleting(item.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filtered.length === 0 && (
                                        <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-600 text-sm">Žiadne zariadenia v tejto kategórii.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {modal && <Modal item={modal === "new" ? null : modal} cats={cats} onClose={() => setModal(null)} onSave={save} />}

                    {deleting && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                            <div className="absolute inset-0 bg-black/70" onClick={() => setDeleting(null)} />
                            <div className="relative bg-nova-surface border border-white/10 rounded-xl p-6 max-w-sm w-full z-10">
                                <h2 className="font-heading font-bold text-white mb-2">Odstrániť zariadenie?</h2>
                                <p className="text-zinc-400 text-sm mb-5">Táto akcia je nevratná.</p>
                                <div className="flex gap-3">
                                    <button onClick={() => setDeleting(null)} className="flex-1 h-10 rounded-md border border-white/15 text-zinc-300 text-sm hover:bg-white/5">Zrušiť</button>
                                    <button onClick={() => del(deleting)} className="flex-1 h-10 rounded-md bg-red-500 text-white font-semibold text-sm hover:bg-red-600">Odstrániť</button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
